(() => {
  if (window.LaunchBuilderInitialized) return;
  window.LaunchBuilderInitialized = true;

  const toInt = (value, fallback = 0) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
  };

  const formatMoney = (cents, currency) => {
    const amount = Number(cents || 0);

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD'
      }).format(amount / 100);
    } catch (error) {
      return `$${(amount / 100).toFixed(2)}`;
    }
  };

  function initSection(section) {
    if (!section || section.dataset.launchBuilderReady === 'true') return;
    section.dataset.launchBuilderReady = 'true';

    const orderRows = Array.from(section.querySelectorAll('[data-order-row]'));
    const rewardCards = Array.from(section.querySelectorAll('[data-reward-card]'));
    const rewardOptions = Array.from(section.querySelectorAll('[data-reward-option]'));
    const summaryItems = section.querySelector('[data-summary-items]');
    const summaryEmpty = section.querySelector('[data-summary-empty]');
    const summaryCases = section.querySelector('[data-summary-cases]');
    const summaryUnits = section.querySelector('[data-summary-units]');
    const summarySubtotal = section.querySelector('[data-summary-subtotal]');
    const summarySavings = section.querySelector('[data-summary-savings]');
    const summaryTotalAfter = section.querySelector('[data-summary-total-after]');
    const mainRequiredNote = section.querySelector('[data-main-required-note]');
    const rewardRequiredNote = section.querySelector('[data-reward-required-note]');
    const rewardChoiceNote = section.querySelector('[data-reward-choice-note]');
    const checkoutButton = section.querySelector('[data-checkout-button]');

    const rootUrl = section.dataset.rootUrl || '/';
    const currency = section.dataset.currency || 'USD';
    const checkoutLabel = section.dataset.checkoutLabel || 'Continue to checkout';
    const emptyLabel = section.dataset.emptyLabel || 'Add at least one wholesale case';
    const unavailableLabel = section.dataset.unavailableLabel || 'Unavailable';
    const rewardRequiredLabel = section.dataset.rewardRequiredLabel || 'Choose your launch support';
    const discountCode = (section.dataset.discountCode || '').trim();
    const discountPercent = Math.max(0, toInt(section.dataset.discountPercent, 0));

    const normalizeQty = (input) => {
      if (!input) return 0;
      const normalized = Math.max(0, toInt(input.value, 0));
      input.value = normalized;
      return normalized;
    };

    const getMainItemState = (row) => {
      const input = row.querySelector('[data-qty-input]');
      const caseQty = normalizeQty(input);
      const available = row.dataset.available === 'true' && Boolean(row.dataset.variantId);
      const unitPriceCents = toInt(row.dataset.unitPriceCents, 0);
      const unitsPerCase = Math.max(1, toInt(row.dataset.unitsPerCase, 12));
      const actualQty = caseQty * unitsPerCase;
      const lineTotal = unitPriceCents * actualQty;

      return {
        row,
        title: row.dataset.title || '',
        variantId: row.dataset.variantId || '',
        available,
        caseQty,
        unitsPerCase,
        actualQty,
        unitPriceCents,
        lineTotal
      };
    };

    const updateOrderRowState = (row) => {
      const state = getMainItemState(row);
      const minus = row.querySelector('[data-qty-minus]');
      const plus = row.querySelector('[data-qty-plus]');
      const input = row.querySelector('[data-qty-input]');

      row.classList.toggle('is-selected', state.available && state.caseQty > 0);
      row.classList.toggle('is-disabled', !state.available);

      if (input) input.disabled = !state.available;
      if (minus) minus.disabled = !state.available;
      if (plus) plus.disabled = !state.available;

      return state;
    };

    const getSelectedMainItems = () => {
      return orderRows
        .map((row) => updateOrderRowState(row))
        .filter((item) => item.available && item.caseQty > 0);
    };

    const getRewardState = (element, subtotal) => {
      const threshold = Math.max(0, toInt(element.dataset.threshold, 0));
      const available = element.dataset.available === 'true' && Boolean(element.dataset.variantId);
      const rewardQuantity = Math.max(1, toInt(element.dataset.rewardQuantity, 1));
      const unlocked = available && subtotal >= threshold;

      return {
        element,
        title: element.dataset.title || '',
        variantId: element.dataset.variantId || '',
        threshold,
        rewardQuantity,
        available,
        unlocked
      };
    };

    const syncRewardUI = (subtotal) => {
      const autoRewardStates = rewardCards.map((card) => {
        const state = getRewardState(card, subtotal);
        const fill = card.querySelector('[data-progress-fill]');
        const status = card.querySelector('[data-progress-status]');
        const progress = state.threshold > 0 ? Math.min(100, (subtotal / state.threshold) * 100) : 100;

        if (fill) {
          fill.style.width = `${Math.max(0, progress)}%`;
        }

        if (status) {
          if (!state.available) {
            status.textContent = unavailableLabel;
          } else if (state.unlocked) {
            status.textContent = 'Unlocked';
          } else {
            status.textContent = `${formatMoney(state.threshold - subtotal, currency)} to unlock`;
          }
        }

        card.classList.toggle('is-unlocked', state.unlocked);
        card.classList.toggle('is-disabled', !state.available);

        return state;
      });

      const optionStates = rewardOptions.map((option) => {
        const state = getRewardState(option, subtotal);
        const input = option.querySelector('[data-reward-radio]');

        option.classList.toggle('is-unlocked', state.unlocked);
        option.classList.toggle('is-disabled', !state.available || !state.unlocked);
        option.classList.toggle('is-selected', Boolean(input && input.checked && !input.disabled));

        if (input) {
          input.disabled = !state.unlocked;
          if (!state.unlocked) input.checked = false;
        }

        return state;
      });

      const autoRewards = autoRewardStates.filter((state) => state.unlocked);
      const unlockedOptions = optionStates.filter((state) => state.unlocked);
      const checkedInput = rewardOptions
        .map((option) => option.querySelector('[data-reward-radio]'))
        .find((input) => input && input.checked && !input.disabled);

      if (unlockedOptions.length === 1) {
        const onlyOption = rewardOptions.find(
          (option) => option.dataset.variantId === unlockedOptions[0].variantId
        );
        const onlyInput = onlyOption?.querySelector('[data-reward-radio]');
        if (onlyInput) onlyInput.checked = true;
      }

      const resolvedCheckedInput = rewardOptions
        .map((option) => option.querySelector('[data-reward-radio]'))
        .find((input) => input && input.checked && !input.disabled);

      optionStates.forEach((state) => {
        const status = state.element.querySelector('[data-reward-status]');
        const input = state.element.querySelector('[data-reward-radio]');
        const selected = Boolean(
          resolvedCheckedInput && input && resolvedCheckedInput.value === input.value
        );

        state.element.classList.toggle('is-selected', selected);

        if (!status) return;

        if (!state.available) {
          status.textContent = unavailableLabel;
        } else if (!state.unlocked) {
          status.textContent = `Locked until ${formatMoney(state.threshold, currency)}`;
        } else if (selected) {
          status.textContent = 'Selected';
        } else {
          status.textContent = 'Ready to select';
        }
      });

      if (rewardChoiceNote) {
        const nextChoiceThreshold = optionStates
          .filter((state) => state.available && !state.unlocked)
          .reduce((min, state) => Math.min(min, state.threshold), Number.POSITIVE_INFINITY);

        if (!optionStates.length) {
          rewardChoiceNote.textContent = 'No final bonus options are configured yet.';
        } else if (!unlockedOptions.length && Number.isFinite(nextChoiceThreshold)) {
          rewardChoiceNote.textContent = `${formatMoney(nextChoiceThreshold - subtotal, currency)} away from unlocking your third launch bonus.`;
        } else if (unlockedOptions.length === 1) {
          rewardChoiceNote.textContent = 'Your order unlocked the final bonus and it is selected automatically.';
        } else if (!checkedInput) {
          rewardChoiceNote.textContent = 'Your order unlocked the final bonus. Choose one option before checkout.';
        } else {
          rewardChoiceNote.textContent = 'Your final bonus is selected and ready to go to checkout.';
        }
      }

      return { autoRewards, unlockedOptions };
    };

    const getSelectedReward = (subtotal) => {
      const checkedInput = rewardOptions
        .map((option) => option.querySelector('[data-reward-radio]'))
        .find((input) => input && input.checked && !input.disabled);

      if (!checkedInput) return null;

      const option = checkedInput.closest('[data-reward-option]');
      if (!option) return null;

      const state = getRewardState(option, subtotal);
      return state.unlocked ? state : null;
    };

    const buildCheckoutUrl = (mainItems, autoRewards, selectedReward) => {
      const normalizedRoot = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
      const lineItems = new Map();

      const appendLineItem = (variantId, quantity) => {
        if (!variantId || quantity <= 0) return;
        lineItems.set(variantId, (lineItems.get(variantId) || 0) + quantity);
      };

      mainItems.forEach((item) => {
        appendLineItem(item.variantId, item.actualQty);
      });

      autoRewards.forEach((reward) => {
        appendLineItem(reward.variantId, reward.rewardQuantity);
      });

      if (selectedReward?.variantId) {
        appendLineItem(selectedReward.variantId, selectedReward.rewardQuantity);
      }

      const cartPath = Array.from(lineItems.entries())
        .map(([variantId, quantity]) => `${variantId}:${quantity}`)
        .join(',');
      const url = new URL(`${normalizedRoot}cart/${cartPath}`, window.location.origin);

      if (discountCode) {
        url.searchParams.set('discount', discountCode);
      }

      return url.toString();
    };

    const renderSummary = (mainItems, autoRewards, selectedReward, subtotal) => {
      if (!summaryItems || !summarySubtotal || !summaryCases || !summaryUnits) return;

      summaryItems.innerHTML = '';

      if (!mainItems.length && !autoRewards.length && !selectedReward) {
        if (summaryEmpty) {
          summaryEmpty.hidden = false;
          summaryItems.appendChild(summaryEmpty);
        }
      } else if (summaryEmpty) {
        summaryEmpty.hidden = true;
      }

      let caseCount = 0;
      let unitCount = 0;

      mainItems.forEach((item) => {
        caseCount += item.caseQty;
        unitCount += item.actualQty;

        const row = document.createElement('div');
        row.className = 'launch-builder__summary-item';

        const copy = document.createElement('div');
        copy.className = 'launch-builder__summary-item-copy';

        const title = document.createElement('span');
        title.className = 'launch-builder__summary-item-title';
        title.textContent = item.title;

        const meta = document.createElement('span');
        meta.className = 'launch-builder__summary-item-meta';
        meta.textContent = `${item.caseQty} case${item.caseQty === 1 ? '' : 's'} / ${item.actualQty} units`;

        copy.appendChild(title);
        copy.appendChild(meta);

        const price = document.createElement('strong');
        price.className = 'launch-builder__summary-item-price';
        price.textContent = formatMoney(item.lineTotal, currency);

        row.appendChild(copy);
        row.appendChild(price);
        summaryItems.appendChild(row);
      });

      autoRewards.forEach((reward) => {
        const row = document.createElement('div');
        row.className = 'launch-builder__summary-item';

        const copy = document.createElement('div');
        copy.className = 'launch-builder__summary-item-copy';

        const title = document.createElement('span');
        title.className = 'launch-builder__summary-item-title';
        title.textContent = reward.title;

        const meta = document.createElement('span');
        meta.className = 'launch-builder__summary-item-meta';
        meta.textContent = 'Automatic launch bonus included';

        copy.appendChild(title);
        copy.appendChild(meta);

        const price = document.createElement('strong');
        price.className = 'launch-builder__summary-item-price';
        price.textContent = 'Included';

        row.appendChild(copy);
        row.appendChild(price);
        summaryItems.appendChild(row);
      });

      if (selectedReward) {
        const row = document.createElement('div');
        row.className = 'launch-builder__summary-item';

        const copy = document.createElement('div');
        copy.className = 'launch-builder__summary-item-copy';

        const title = document.createElement('span');
        title.className = 'launch-builder__summary-item-title';
        title.textContent = selectedReward.title;

        const meta = document.createElement('span');
        meta.className = 'launch-builder__summary-item-meta';
        meta.textContent = 'Final bonus selected';

        copy.appendChild(title);
        copy.appendChild(meta);

        const price = document.createElement('strong');
        price.className = 'launch-builder__summary-item-price';
        price.textContent = 'Included';

        row.appendChild(copy);
        row.appendChild(price);
        summaryItems.appendChild(row);
      }

      const savings = Math.round((subtotal * discountPercent) / 100);
      const totalAfter = subtotal - savings;

      summaryCases.textContent = String(caseCount);
      summaryUnits.textContent = String(unitCount);
      summarySubtotal.textContent = formatMoney(subtotal, currency);
      if (summarySavings) summarySavings.textContent = formatMoney(savings, currency);
      if (summaryTotalAfter) summaryTotalAfter.textContent = formatMoney(totalAfter, currency);
    };

    const disableCheckout = (label) => {
      if (!checkoutButton) return;
      checkoutButton.textContent = label;
      checkoutButton.removeAttribute('href');
      checkoutButton.setAttribute('aria-disabled', 'true');
    };

    const enableCheckout = (url) => {
      if (!checkoutButton) return;
      checkoutButton.textContent = checkoutLabel;
      checkoutButton.href = url;
      checkoutButton.setAttribute('aria-disabled', 'false');
    };

    const updateAll = () => {
      const mainItems = getSelectedMainItems();
      const subtotal = mainItems.reduce((sum, item) => sum + item.lineTotal, 0);
      const { autoRewards, unlockedOptions } = syncRewardUI(subtotal);
      const selectedReward = getSelectedReward(subtotal);

      renderSummary(mainItems, autoRewards, selectedReward, subtotal);

      if (mainRequiredNote) {
        mainRequiredNote.hidden = mainItems.length > 0;
      }

      if (rewardRequiredNote) {
        rewardRequiredNote.hidden = !(unlockedOptions.length > 1 && !selectedReward);
      }

      if (!mainItems.length) {
        disableCheckout(emptyLabel);
        return;
      }

      if (mainItems.some((item) => !item.variantId)) {
        disableCheckout(unavailableLabel);
        return;
      }

      if (unlockedOptions.length > 1 && !selectedReward) {
        disableCheckout(rewardRequiredLabel);
        return;
      }

      enableCheckout(buildCheckoutUrl(mainItems, autoRewards, selectedReward));
    };

    if (checkoutButton) {
      checkoutButton.addEventListener('click', (event) => {
        if (checkoutButton.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
        }
      });
    }

    orderRows.forEach((row) => {
      const input = row.querySelector('[data-qty-input]');
      const minus = row.querySelector('[data-qty-minus]');
      const plus = row.querySelector('[data-qty-plus]');

      minus?.addEventListener('click', () => {
        if (!input) return;
        input.value = Math.max(0, normalizeQty(input) - 1);
        updateAll();
      });

      plus?.addEventListener('click', () => {
        if (!input) return;
        input.value = normalizeQty(input) + 1;
        updateAll();
      });

      input?.addEventListener('input', () => {
        normalizeQty(input);
        updateAll();
      });

      updateOrderRowState(row);
    });

    rewardOptions.forEach((option) => {
      const input = option.querySelector('[data-reward-radio]');
      input?.addEventListener('change', () => {
        updateAll();
      });
    });

    updateAll();
  }

  const initAllSections = () => {
    document.querySelectorAll('[data-launch-builder]').forEach((section) => {
      initSection(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllSections, { once: true });
  } else {
    initAllSections();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section =
      event.target.matches?.('[data-launch-builder]')
        ? event.target
        : event.target.querySelector?.('[data-launch-builder]');
    initSection(section);
  });
})();
